// forecastWizardHelpers.js — pure helpers for ForecastConfigWizard (no React deps)
//
// EDA defaults sourced from:
//   correlation_threshold: 0.95  — services/python/forecast-worker/src/models.py:47
//   weak_signal_threshold: 0.02  — services/python/forecast-worker/src/models.py:48
//   pairplot_top_n: 15           — forecast-workbench-spec.md:227
//   umap_components: 2           — forecast-workbench-spec.md:227

// ─── Initial state ────────────────────────────────────────────────────────────

export const INITIAL_WIZARD_STATE = {
  forecast_type: 'binary',
  stage_progression: { from_stage: '', to_stage: '' },
  regression: { target_col: '' },
  multi_class: { class_col: '', classes: [] },
  survival: { duration_col: '', event_col: '', strata_col: '' },
  prediction: {
    view: '',
    account_col: '', opened_at_col: '', closed_at_col: '',
    label_col: '', label_value: '',
    horizon_days: 90,
    record_id_col: '',   // Optional — blank omitted from payload; does not block save
  },
  event_sources: [],    // EventSourceDraft[] — columns NOT stored here; see viewValidations
  windows: [14, 30, 90],
  aggregations: ['count', 'rate'],
  user_features: [],    // UserFeatureDraft[]
  excluded_features: [],
  training: {
    auto_split: true,   // UI-only; stripped by buildForecastConfig
    split_date: '',
    num_leaves: 31, num_iterations: 100,
    learning_rate: 0.1, min_data_in_leaf: 20,
  },
  eda: {
    correlation_threshold: 0.95,
    weak_signal_threshold: 0.02,
    sparse_event_threshold: 5,
    imbalance_threshold: 0.1,
    pairplot_top_n: 15,
    umap_components: 2,
  },
};

// EventSourceDraft shape:
// { view, account_col, occurred_at_col, event_type_col, event_type_literal,
//   direction_col, result_col, result_type:'boolean'|'numeric', topic_col }

// UserFeatureDraft shape (mirrors UserFeature at forecast_config.go:65 + result_eq_ui):
// { name, window, aggregation, event_source_index,
//   filter: { event_type, topic_id, topic_config_name, direction,
//             result_eq_ui: 'any'|'true'|'false' } }
// result_eq_ui → FeatureFilter.ResultEq *bool:
//   'any' → undefined   'true' → true   'false' → false

// Mirrors forecast_config.go:277 — the only aggregations the backend accepts.
export const VALID_AGGREGATIONS = ['count', 'rate', 'avg', 'recency', 'stddev'];

// ─── Window input parsing ──────────────────────────────────────────────────────

export function parseWindowInput(raw) {
  // Returns a positive integer or null if invalid.
  const n = parseInt(String(raw).trim(), 10);
  if (isNaN(n) || n <= 0) return null;
  return n;
}

export function addWindowToSet(existing, raw) {
  // Returns deduplicated, ascending-sorted array.
  // Returns existing unchanged if raw is invalid.
  const n = parseWindowInput(raw);
  if (n === null) return existing;
  const merged = [...new Set([...existing, n])];
  merged.sort((a, b) => a - b);
  return merged;
}

// ─── Config builder ───────────────────────────────────────────────────────────

export function buildForecastConfig(state) {
  // Strips UI-only fields (auto_split). configDirty is not in state.
  const ft = state.forecast_type || 'binary';
  return {
    forecast_type: ft,
    ...(ft === 'stage_progression' &&
        (state.stage_progression?.from_stage || state.stage_progression?.to_stage)
      ? { stage_progression: state.stage_progression } : {}),
    ...(ft === 'time_to_close' && state.regression?.target_col
      ? { regression: { target_col: state.regression.target_col } } : {}),
    ...(ft === 'multi_class' && state.multi_class?.class_col
      ? { multi_class: { class_col: state.multi_class.class_col,
                         classes: state.multi_class.classes || [] } } : {}),
    prediction: {
      view: state.prediction.view,
      account_col: state.prediction.account_col,
      opened_at_col: state.prediction.opened_at_col,
      closed_at_col: state.prediction.closed_at_col,
      label_col: state.prediction.label_col,
      label_value: state.prediction.label_value,
      horizon_days: Number(state.prediction.horizon_days),
      // record_id_col: omit if blank (backend validates only when present: handlers_forecast.go:343)
      ...(state.prediction.record_id_col ? { record_id_col: state.prediction.record_id_col } : {}),
      // survival fields: only included when forecast_type === 'survival'
      ...(ft === 'survival' && state.survival?.duration_col ? { duration_col: state.survival.duration_col } : {}),
      ...(ft === 'survival' && state.survival?.event_col ? { event_col: state.survival.event_col } : {}),
      ...(ft === 'survival' && state.survival?.strata_col ? { strata_col: state.survival.strata_col } : {}),
    },
    event_sources: state.event_sources.map(es => ({
      view: es.view,
      account_col: es.account_col,
      occurred_at_col: es.occurred_at_col,
      event_type_col: es.event_type_col || undefined,
      event_type_literal: es.event_type_literal || undefined,
      direction_col: es.direction_col || undefined,
      result_col: es.result_col || undefined,
      result_type: es.result_col ? es.result_type : undefined,
      topic_col: es.topic_col || undefined,
    })),
    windows: state.windows,          // already validated: positive ints, sorted, deduped
    aggregations: state.aggregations,
    user_features: state.user_features.length > 0
      ? state.user_features.map(uf => ({
          name: uf.name,
          window: Number(uf.window),
          aggregation: uf.aggregation,
          event_source_index: uf.event_source_index,
          filter: {
            event_type: uf.filter.event_type || undefined,
            topic_id: uf.filter.topic_id || undefined,
            topic_config_name: uf.filter.topic_config_name || undefined,
            direction: uf.filter.direction || undefined,
            result_eq: uf.filter.result_eq_ui === 'any' ? undefined
              : uf.filter.result_eq_ui === 'true' ? true : false,
          },
        }))
      : undefined,
    excluded_features: state.excluded_features.length > 0
      ? state.excluded_features : undefined,
    training: {
      split_date: state.training.auto_split ? '' : state.training.split_date,
      num_leaves: Number(state.training.num_leaves),
      num_iterations: Number(state.training.num_iterations),
      learning_rate: Number(state.training.learning_rate),
      min_data_in_leaf: Number(state.training.min_data_in_leaf),
    },
    eda: {
      correlation_threshold: Number(state.eda.correlation_threshold),
      weak_signal_threshold: Number(state.eda.weak_signal_threshold),
      sparse_event_threshold: Number(state.eda.sparse_event_threshold),
      imbalance_threshold: Number(state.eda.imbalance_threshold),
      pairplot_top_n: Number(state.eda.pairplot_top_n),
      umap_components: Number(state.eda.umap_components),
    },
  };
}

// ─── Config parser ────────────────────────────────────────────────────────────

export function configToWizardState(config) {
  if (!config) return INITIAL_WIZARD_STATE;
  return {
    forecast_type: config.forecast_type || 'binary',
    stage_progression: {
      from_stage: config.stage_progression?.from_stage ?? '',
      to_stage: config.stage_progression?.to_stage ?? '',
    },
    regression: { target_col: config.regression?.target_col ?? '' },
    multi_class: {
      class_col: config.multi_class?.class_col ?? '',
      classes: config.multi_class?.classes ?? [],
    },
    survival: {
      duration_col: config.prediction?.duration_col ?? '',
      event_col: config.prediction?.event_col ?? '',
      strata_col: config.prediction?.strata_col ?? '',
    },
    prediction: {
      view: config.prediction?.view ?? '',
      account_col: config.prediction?.account_col ?? '',
      opened_at_col: config.prediction?.opened_at_col ?? '',
      closed_at_col: config.prediction?.closed_at_col ?? '',
      label_col: config.prediction?.label_col ?? '',
      label_value: config.prediction?.label_value ?? '',
      horizon_days: config.prediction?.horizon_days ?? 90,
      record_id_col: config.prediction?.record_id_col ?? '',
    },
    event_sources: (config.event_sources || []).map(es => ({
      view: es.view ?? '',
      account_col: es.account_col ?? '',
      occurred_at_col: es.occurred_at_col ?? '',
      event_type_col: es.event_type_col ?? '',
      event_type_literal: es.event_type_literal ?? '',
      direction_col: es.direction_col ?? '',
      result_col: es.result_col ?? '',
      result_type: es.result_type ?? 'boolean',
      topic_col: es.topic_col ?? '',
    })),
    windows: config.windows ?? [14, 30, 90],
    aggregations: config.aggregations ?? ['count', 'rate'],
    user_features: (config.user_features ?? []).map(uf => ({
      name: uf.name ?? '',
      window: uf.window ?? 30,
      aggregation: uf.aggregation ?? 'count',
      event_source_index: uf.event_source_index ?? 0,
      filter: {
        event_type: uf.filter?.event_type ?? '',
        topic_id: uf.filter?.topic_id ?? '',
        topic_config_name: uf.filter?.topic_config_name ?? '',
        direction: uf.filter?.direction ?? '',
        result_eq_ui: uf.filter?.result_eq == null ? 'any'
          : uf.filter.result_eq ? 'true' : 'false',
      },
    })),
    excluded_features: config.excluded_features ?? [],
    training: {
      auto_split: !config.training?.split_date,
      split_date: config.training?.split_date ?? '',
      num_leaves: config.training?.num_leaves ?? 31,
      num_iterations: config.training?.num_iterations ?? 100,
      learning_rate: config.training?.learning_rate ?? 0.1,
      min_data_in_leaf: config.training?.min_data_in_leaf ?? 20,
    },
    eda: {
      correlation_threshold: config.eda?.correlation_threshold ?? 0.95,
      weak_signal_threshold: config.eda?.weak_signal_threshold ?? 0.02,
      sparse_event_threshold: config.eda?.sparse_event_threshold ?? 5,
      imbalance_threshold: config.eda?.imbalance_threshold ?? 0.1,
      pairplot_top_n: config.eda?.pairplot_top_n ?? 15,
      umap_components: config.eda?.umap_components ?? 2,
    },
  };
}

// ─── Step validators ──────────────────────────────────────────────────────────

export function validateName(name) {
  return name.trim() ? [] : ['Name is required.'];
}

// Step 0: Forecast type selection (new step inserted before existing steps)
export function validateForecastType(state) {
  if (!state.forecast_type) return ['Select a forecast type to continue.'];
  return [];
}

// Step 1 (was step 0): Prediction source
export function validateStep0(state) {
  const errs = [];
  if (!state.prediction.view) errs.push('Prediction view is required.');
  if (!state.prediction.account_col) errs.push('Account column is required.');
  if (!state.prediction.opened_at_col) errs.push('Opened At column is required.');
  if (!state.prediction.closed_at_col) errs.push('Closed At column is required.');
  if (state.forecast_type === 'survival') {
    if (!state.survival?.duration_col) errs.push('Duration column is required for survival forecast.');
    if (!state.survival?.event_col) errs.push('Event indicator column is required for survival forecast.');
  } else if (state.forecast_type !== 'time_to_close' && state.forecast_type !== 'multi_class') {
    if (!state.prediction.label_col) errs.push('Label column is required.');
    if (!state.prediction.label_value) errs.push('Label value is required.');
  }
  if (state.forecast_type === 'time_to_close' && !state.regression?.target_col) {
    errs.push('Target column is required for time-to-close forecast.');
  }
  if (state.forecast_type === 'multi_class' && !state.multi_class?.class_col) {
    errs.push('Class column is required for multi-class forecast.');
  }
  if (state.forecast_type === 'multi_class' && (!state.multi_class?.classes || state.multi_class.classes.length < 2)) {
    errs.push('At least 2 class values are required for multi-class forecast.');
  }
  // record_id_col: NOT an error — optional per backend contract (forecast_config.go:223-282).
  // Warning shown inline in UI; does not block Next/Save.
  if (!state.prediction.horizon_days || Number(state.prediction.horizon_days) <= 0)
    errs.push('Horizon must be a positive number of days.');
  return errs;
}

export function validateStep1(state) {
  if (state.event_sources.length === 0) return []; // event sources are optional
  const errs = [];
  state.event_sources.forEach((es, i) => {
    if (!es.view) errs.push(`Event source ${i + 1}: view is required.`);
    if (!es.account_col) errs.push(`Event source ${i + 1}: account column is required.`);
    if (!es.occurred_at_col) errs.push(`Event source ${i + 1}: occurred-at column is required.`);
  });
  return errs;
}

export function validateStep2(state) {
  const errs = [];
  if (state.windows.length === 0) errs.push('At least one time window is required.');
  if (state.aggregations.length === 0) errs.push('At least one aggregation is required.');
  state.aggregations.forEach(agg => {
    if (!VALID_AGGREGATIONS.includes(agg))
      errs.push(`Unknown aggregation "${agg}" — must be one of: ${VALID_AGGREGATIONS.join(', ')}.`);
  });
  return errs;
}

// validateStep3 mirrors forecast_config.go:261-268 — catches index bounds and topic coupling.
export function validateStep3(state) {
  const errs = [];
  state.user_features.forEach((uf, i) => {
    if (uf.event_source_index < 0 || uf.event_source_index >= state.event_sources.length)
      errs.push(`Custom feature "${uf.name || i + 1}" references a removed event source.`);
    if (uf.filter.topic_id && !uf.filter.topic_config_name)
      errs.push(`Custom feature "${uf.name || i + 1}": topic config name required when topic ID is set.`);
  });
  return errs;
}

export function validateStep4(state) {
  if (!state.training.auto_split && !state.training.split_date)
    return ['Split date is required when not using automatic split.'];
  return [];
}

export function validateStep5(_state) { return []; }

// STEP_VALIDATORS[0] = new Type step; [1..6] = original steps shifted by 1
export const STEP_VALIDATORS = [
  validateForecastType, validateStep0, validateStep1,
  validateStep2, validateStep3, validateStep4, validateStep5,
];

// Returns the first wizard step whose view has a non-transient blocking error,
// or null if no views are blocked. Used by handleNext and handleSubmit.
// Prediction is now step 1 (shifted +1 by new Type step at 0).
export function firstBlockingViewStep(wizardState, viewValidations) {
  const isBlocked = (viewId) => {
    const vv = viewValidations[viewId];
    return !!(vv && (vv.permissionDenied || (vv.valid === false && !vv.serverError)));
  };
  if (isBlocked(wizardState.prediction.view)) return 1; // was 0
  if (wizardState.event_sources.some(es => isBlocked(es.view))) return 2; // was 1
  return null;
}

// ─── Server error routing ──────────────────────────────────────────────────────
// Maps server error codes + field prefixes to wizard step indices (0–5 or 'global').
// Covers all field keys returned by validateForecastConfig (forecast_config.go:223-282).

// Maps server error codes + field prefixes to wizard step indices (0–6 or 'global').
// All indices shifted +1 vs original due to new Type step at index 0.
export function deriveStepFromServerError(errorData) {
  const code = errorData?.error;
  const msg = errorData?.message ?? '';
  if (code === 'duckdb_not_available') return 'global';
  if (code === 'config_locked') return 'global';
  if (code === 'invalid_view' || code === 'invalid_view_columns' || code === 'ambiguous_object') {
    return msg.includes('event_sources') ? 2 : 1; // event_sources→2, prediction→1
  }
  if (code === 'invalid_request' && errorData?.fields) {
    const keys = Object.keys(errorData.fields);
    // Check in priority order matching wizard step sequence
    if (keys.some(k => k === 'forecast_type')) return 0;
    if (keys.some(k => k.startsWith('regression.') || k.startsWith('multi_class.'))) return 1;
    if (keys.some(k => k.startsWith('prediction'))) return 1; // was 0
    if (keys.some(k => k.startsWith('event_sources'))) return 2; // was 1
    if (keys.some(k => k === 'windows' || k === 'aggregations')) return 3; // was 2
    if (keys.some(k => k.startsWith('user_features'))) return 4; // was 3
    if (keys.some(k => k.startsWith('training'))) return 5; // was 4
  }
  return 'global';
}
